import React from "react";

import { Type } from "common/entities/IMessage";
import { FlexJustify } from "common/fragments/Flex";
import Loading from "common/fragments/Loading";
import NonIdealState from "common/fragments/NonIdealState";
import { HTML } from "common/fragments/TextContent";
import { BlockVariant, BlockView } from "common/views/block/BlockView.view";
import { ButtonVariant, ButtonView } from "common/views/button/Button.view";
import { CardVariant, CardView } from "common/views/card/Card.view";
import { DropdownSelectVariant } from "common/views/dropdownSelect/DropdownSelect.view";
import { OptionDropdownSelect } from "common/views/dropdownSelect/OptionDropdownSelect";
import HeadingView, { HeadingVariant } from "common/views/heading/Heading.view";
import { Query } from "core/api/typedQuery";
import DateLabel from "core/components/label/DateLabel";
import { IPanelProps, Panel } from "core/decorators/Panel";
import { isEmpty } from "core/util/ArrayUtils";
import { validateEnum, validateList } from "core/util/ParamUtil";
import TokenSession from "core/util/session/TokenSession";
import StringUtil from "core/util/StringUtil";
import { iDef, iDefL, nDef } from "core/util/TypeUtils";
import { IMessageParams, Message } from "./entities/Message";
import { IOrganizationParams, Organization } from "./entities/Organization";
import { PARAM_SELECTED_MESSAGE } from "./MessageEdit.panel";
import { MessageListConfig } from "./MessageListConfig";
import labelProvider from './MessageList.labels';

const PARAM_ORG_MESSAGE_PAGE_SIZE = "org.message.pageSize";
const PARAM_ORG_MESSAGE_SORT = "org.message.sort";
const PARAM_ORG_MESSAGE_TYPE = "org.message.type";
const { Labels } = labelProvider;

const sortOptions = [
	{value: "-startDate", label: <Labels.PostDate />},
	{value: "name", label: <Labels.Title/>}
];

const typeOptions = [
	{value: undefined, label: <Labels.All />},
	{value: Type.ANNOUNCEMENT, label: <Labels.Announcement />},
	{value: Type.INFO, label: <Labels.Info />},
	{value: Type.REMINDER, label: <Labels.Reminder />},
	{value: Type.CRITICAL, label: <Labels.Critical />},
	{value: Type.ALERT, label: <Labels.Alert/>}
];

export const OrgQuery = Query.queryOne<Organization, IOrganizationParams>(Organization);
let OrgMessagesQuery = Query.queryMany<Message, IMessageParams>(Message);

@Panel({id: 'organization-message-list', configClass: MessageListConfig, pageParams: true,labelProvider})
export class OrganizationMessageList extends React.Component<IPanelProps<MessageListConfig>> {

	private userId = TokenSession.getUserId();
constructor(props){
	super(props);
	this.state={
		messageid:null
	}
}
	public render() {
		const { config, pageParams } = this.props;

		const { guid } = pageParams;

		if (nDef(guid)) {
			return (
				<NonIdealState
					title={<Labels.OrganizationEmpty />}
					description={<Labels.DescriptionEmpty />}
				/>
			);
		}

		// get the org group id
		return (
			<OrgQuery params={{guid}}>
				{(orgArgs) => {
					if (orgArgs.loading) {
							return <Loading />;
						}

					if (!orgArgs.data || !orgArgs.data.group) {
						return (
							<NonIdealState
								title={<Labels.OrganizationEmpty/>}
								description={<Labels.OrganizationNotfoundDescription />}
							/>
						);
					}

					const defaultPageSize = iDef(config.pageSize) ? config.pageSize : 10;
					const currentPageSize = StringUtil.toInteger(pageParams[PARAM_ORG_MESSAGE_PAGE_SIZE], defaultPageSize);

					const params = {
						targetGroupIds: [orgArgs.data.group.id],
						pageSize: currentPageSize,
						sort: validateList(['-startDate', 'name'], pageParams[PARAM_ORG_MESSAGE_SORT], '-startDate'),
						type: validateEnum(Type, pageParams[PARAM_ORG_MESSAGE_TYPE], null)
					};

					// if group found, get the messages by target group
					return [
						<BlockView key="refine" variant={BlockVariant.REFINE} viewKey="refine">
							<OptionDropdownSelect
								options={sortOptions}
								onChange={this.onChangeParam(PARAM_ORG_MESSAGE_SORT)}
								value={pageParams[PARAM_ORG_MESSAGE_SORT]}
								variant={DropdownSelectVariant.MINIMAL}
								viewKey="refine-sort"
							>
								<Labels.Sort />
							</OptionDropdownSelect>
							<OptionDropdownSelect
								key="refine-type"
								options={typeOptions}
								onChange={this.onChangeParam(PARAM_ORG_MESSAGE_TYPE)}
								value={pageParams[PARAM_ORG_MESSAGE_TYPE]}
								variant={DropdownSelectVariant.MINIMAL}
								viewKey="refine.type"
							>
								<Labels.Type />
							</OptionDropdownSelect>
						</BlockView>,
						<div key="data">
							<OrgMessagesQuery key="query" params={params}>
								{({data, loading}) => {
									if (loading) {
										return <Loading />;
									}

									if (!data) {
										return (
											<NonIdealState
												title={<Labels.OrganizationEmpty />}
												description={<Labels.OrganizationNotfoundDescription />}
											/>
										);
									}

									if (isEmpty(data)) {
										return (
											<NonIdealState
												title={<Labels.MessageEmpty />}
												description={<Labels.MessageEmptyDescription />}
											/>
										);
									}
									OrgMessagesQuery = Query.queryMany<Message, IMessageParams>(Message);
									return (
										<React.Fragment>
											{data.map(this.renderMessage)}
											{
												data.length >= currentPageSize && (
													<BlockView variant={BlockVariant.PAGING} viewKey="paging">
														<ButtonView
															onClick={() => this.onChangeParam(PARAM_ORG_MESSAGE_PAGE_SIZE)(currentPageSize + defaultPageSize)}
															variant={ButtonVariant.PRIMARY}
															viewKey="paging.loadmore"
														>
															<Labels.LoadMore />
														</ButtonView>
													</BlockView>
												)
											}
										</React.Fragment>
									);
								}}
							</OrgMessagesQuery>
						</div>
					];
				}}
			</OrgQuery>
		);
	}

	private renderMessage = (message: Message) => {
		// find a better way to do this:
		const canEdit = message.createdBy !== null && message.createdBy.id + '' === this.userId + '';
		return (
			<CardView key={message.id} variant={CardVariant.SURFACE} viewKey="card">
				<BlockView variant={BlockVariant.CARD_HEADER} viewKey="card-header">
					<FlexJustify>
						<div>
							<React.Fragment>
								<Labels.CreatedBy />
								{' '}
								{
									message.createdBy ?
										(message.createdBy.firstName + ' ' + message.createdBy.lastName)
										: 'UNKNOWN'
								}
							</React.Fragment>
						</div>
						<div>
							<DateLabel value={message.startDate} />
						</div>
					</FlexJustify>
				</BlockView>
				<BlockView variant={BlockVariant.CARD_CONTENT} viewKey="card-content">
					<HeadingView viewKey="title" children={message.name} />
					<HTML content={message.summary} />
					<HTML content={message.description} />
				</BlockView>
				<BlockView variant={BlockVariant.CARD_ACTIONS} viewKey="card-actions">
					{
						canEdit && (
							<ButtonView
								onClick={() => this.onChangeParam(PARAM_SELECTED_MESSAGE)(message.id)}
								variant={ButtonVariant.CARD_ACTION}
								viewKey="edit-button"
							>
								<Labels.Edit />
							</ButtonView>
						)
					}
				</BlockView>
			</CardView>
		);
	}

	private onChangeParam = (name: string) => (value: any) => {
		if (iDefL(value)) {
			this.props.actions.pushPageParam(name, value);
	 } else {
			this.props.actions.removePageParam(name);
		}
	}
}



// WEBPACK FOOTER //
// ./src/panels/organizationAdmin/MessageList.panel.tsx